/**
 * localStorage-backed POI cache.
 * Key shape: poi:{category}:{lat3}_{lng3}_{radius}
 * TTL: 48 hours.
 *
 * BOUNDED ON PURPOSE. The key includes the location rounded to ~110m, so a
 * single drive mints a new key every block and the old ones are only ever
 * dropped if that exact cell is visited again inside the TTL -- which it
 * almost never is. Left unbounded this cache fills the whole localStorage
 * quota within about a week of ordinary use, and once the quota is gone
 * EVERY other write fails: the Supabase auth token (the user can no longer
 * sign in) and the saved-trips store (a trip the user believes they saved
 * is not on the device). Measured full at 10 MB on Aug 23; observed on
 * Aug 27 as a dead sign-in page and a frozen app.
 *
 * So: sweep at startup, sweep before giving up on a refused write, and never
 * hold more than MAX_ENTRIES cells.
 */
const TTL_MS = 48 * 60 * 60 * 1000;
const PREFIX = "poi:";

/** Hard ceiling on cached cells. Sized to stay well inside any quota. */
const MAX_ENTRIES = 150;

function round(n: number, p = 3) {
  const f = Math.pow(10, p);
  return Math.round(n * f) / f;
}

export function poiKey(category: string, lat: number, lng: number, radius: number): string {
  return `${PREFIX}${category}:${round(lat)}_${round(lng)}_${radius}`;
}

/** Every poi: key with its write time. Unparseable entries get time 0 (evict first). */
function entries(): { key: string; t: number }[] {
  const out: { key: string; t: number }[] = [];
  for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i);
    if (!key || !key.startsWith(PREFIX)) continue;
    let t = 0;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw) t = (JSON.parse(raw) as { t?: number }).t ?? 0;
    } catch { /* corrupt -- evict first */ }
    out.push({ key, t });
  }
  return out;
}

/**
 * Drop expired cells, then oldest-first until the cache is under `keep`.
 * Returns how many keys were removed. Safe to call at any time.
 */
export function sweepPoiCache(keep = MAX_ENTRIES): number {
  if (typeof window === "undefined") return 0;
  let removed = 0;
  try {
    const now = Date.now();
    const all = entries();

    const live: { key: string; t: number }[] = [];
    for (const e of all) {
      if (now - e.t > TTL_MS) {
        window.localStorage.removeItem(e.key);
        removed++;
      } else {
        live.push(e);
      }
    }

    if (live.length > keep) {
      live.sort((a, b) => a.t - b.t); // oldest first
      for (const e of live.slice(0, live.length - keep)) {
        window.localStorage.removeItem(e.key);
        removed++;
      }
    }
  } catch { /* best effort */ }
  return removed;
}

export function readCache<T>(key: string): T | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const { t, v } = JSON.parse(raw) as { t: number; v: T };
    if (Date.now() - t > TTL_MS) {
      window.localStorage.removeItem(key);
      return null;
    }
    return v;
  } catch {
    return null;
  }
}

export function writeCache<T>(key: string, value: T): void {
  if (typeof window === "undefined") return;
  const payload = JSON.stringify({ t: Date.now(), v: value });
  try {
    window.localStorage.setItem(key, payload);
    return;
  } catch {
    // Quota. Make room from our OWN keys rather than dying quietly -- a
    // silent failure here is what lets the cache sit at the ceiling and
    // starve the auth token.
  }
  sweepPoiCache(Math.floor(MAX_ENTRIES / 2));
  try {
    window.localStorage.setItem(key, payload);
  } catch {
    // Still no room: something other than this cache is filling the quota.
    // Drop the write; the cache is an optimisation, not a store of record.
  }
}
